import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface RosterViolation {
  user_id: number;
  username: string;
  violations: Array<{
    type: string;
    details: {
      actual: number | string;
      max: number | string;
      excess: number | string;
    };
  }>;
}

interface RosterViolationsProps {
  language: string;
}

const VIOLATION_MESSAGES_FR: { [key: string]: string } = {
  too_many_forwards: "Trop d'attaquants dans l'alignement",
  too_many_defenders: "Trop de défenseurs dans l'alignement",
  too_many_goalies: "Trop de gardiens dans l'alignement",
  not_enough_forwards: "Pas assez d'attaquants dans l'alignement",
  not_enough_defenders: "Pas assez de défenseurs dans l'alignement",
  not_enough_goalies: "Pas assez de gardiens dans l'alignement",
  salary_cap_exceeded: "Plafond salarial dépassé (alignement principal)",
  salary_cap_reserve_exceeded: "Plafond salarial dépassé (réserve)",
  too_many_reserve_players: "Trop de joueurs en réserve"
};

const VIOLATION_MESSAGES_EN: { [key: string]: string } = {
  too_many_forwards: "Too many forwards in lineup",
  too_many_defenders: "Too many defenders in lineup",
  too_many_goalies: "Too many goalies in lineup",
  not_enough_forwards: "Not enough forwards in lineup",
  not_enough_defenders: "Not enough defenders in lineup",
  not_enough_goalies: "Not enough goalies in lineup",
  salary_cap_exceeded: "Salary cap exceeded (main lineup)",
  salary_cap_reserve_exceeded: "Salary cap exceeded (reserve)",
  too_many_reserve_players: "Too many reserve players"
};

function formatSalary(salary: number): string {
  return `${(salary / 1_000_000).toFixed(2)}M$`;
}

export function RosterViolations({ language }: RosterViolationsProps) {
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { isStandardFlexible } = useLeagueMode();
  const [violations, setViolations] = useState<RosterViolation[]>([]);
  const [loading, setLoading] = useState(true);

  const violationMessages = language === 'fr' ? VIOLATION_MESSAGES_FR : VIOLATION_MESSAGES_EN;

  useEffect(() => {
    if (!selectedLeague) return;
    checkViolations();
  }, [selectedLeague]);

  async function checkViolations() {
    if (!selectedLeague) return;
    // Pas de validation d'alignement pour les ligues standard_flexible
    if (isStandardFlexible) {
      setViolations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const foundViolations: RosterViolation[] = [];

      // 1. Récupérer les paramètres de la ligue
      const { data: settings, error: settingsError } = await supabase
        .from('league_settings')
        .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, number_foward, number_def, number_goalie, number_reserve')
        .eq('league_id', selectedLeague.id)
        .single();

      if (settingsError) throw settingsError;

      // 2. Récupérer tous les utilisateurs de la ligue
      const { data: leagueUsers, error: usersError } = await supabase
        .from('leagues_users')
        .select('user_id')
        .eq('league_id', selectedLeague.id);

      if (usersError) throw usersError;

      // 3. Récupérer les usernames depuis la table users
      const userIds = (leagueUsers || []).map(u => u.user_id);
      const { data: usersData, error: usersDataError } = await supabase
        .from('users')
        .select('user_id, username')
        .in('user_id', userIds);

      if (usersDataError) throw usersDataError;

      // Créer un map pour accéder rapidement aux usernames
      const usernameMap = new Map<number, string>();
      (usersData || []).forEach(u => {
        usernameMap.set(u.user_id, u.username);
      });

      // 4. Pour chaque utilisateur, vérifier les violations
      for (const leagueUser of leagueUsers || []) {
        const userViolations: Array<{ type: string; details: any }> = [];
        const username = usernameMap.get(leagueUser.user_id) || 'Unknown';

        // Vérifier le nombre de joueurs par position
        const { data: playerCounts } = await supabase
          .from('get_number_type_player_per_team')
          .select('number_of_fowards, number_of_defenders')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', leagueUser.user_id)
          .single();

        if (playerCounts) {
          const maxForwards = settings?.number_foward || 9;
          const actualForwards = playerCounts.number_of_fowards || 0;
          if (actualForwards > maxForwards) {
            userViolations.push({
              type: 'too_many_forwards',
              details: {
                actual: actualForwards,
                max: maxForwards,
                excess: actualForwards - maxForwards
              }
            });
          }
          if (actualForwards < maxForwards) {
            userViolations.push({
              type: 'not_enough_forwards',
              details: {
                actual: actualForwards,
                max: maxForwards,
                excess: maxForwards - actualForwards
              }
            });
          }

          const maxDefenders = settings?.number_def || 6;
          const actualDefenders = playerCounts.number_of_defenders || 0;
          if (actualDefenders > maxDefenders) {
            userViolations.push({
              type: 'too_many_defenders',
              details: {
                actual: actualDefenders,
                max: maxDefenders,
                excess: actualDefenders - maxDefenders
              }
            });
          }
          if (actualDefenders < maxDefenders) {
            userViolations.push({
              type: 'not_enough_defenders',
              details: {
                actual: actualDefenders,
                max: maxDefenders,
                excess: maxDefenders - actualDefenders
              }
            });
          }
        }

        // Vérifier le nombre de gardiens
        const { data: goalieCounts } = await supabase
          .from('get_number_type_goalie_per_team')
          .select('number_of_goalie')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', leagueUser.user_id)
          .single();

        if (goalieCounts) {
          const maxGoalies = settings?.number_goalie || 3;
          const actualGoalies = goalieCounts.number_of_goalie || 0;
          if (actualGoalies > maxGoalies) {
            userViolations.push({
              type: 'too_many_goalies',
              details: {
                actual: actualGoalies,
                max: maxGoalies,
                excess: actualGoalies - maxGoalies
              }
            });
          }
          if (actualGoalies < maxGoalies) {
            userViolations.push({
              type: 'not_enough_goalies',
              details: {
                actual: actualGoalies,
                max: maxGoalies,
                excess: maxGoalies - actualGoalies
              }
            });
          }
        }

        // Vérifier le plafond salarial (alignement principal, excluant les joueurs LTIR)
        const { data: mainPlayers } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', leagueUser.user_id)
          .eq('isnhl', true)
          .eq('is_injured', false);

        if (mainPlayers && mainPlayers.length > 0) {
          const playerIds = mainPlayers.map(p => p.player_id);
          
          let totalSalaryMain = 0;

          // Salaires des joueurs (skaters)
          const { data: skaters } = await supabase
            .from('show_player_onlycurrentday')
            .select('salary')
            .in('playerid', playerIds);

          if (skaters) {
            totalSalaryMain += skaters.reduce((sum, p) => sum + (p.salary || 0), 0);
          }

          // Salaires des gardiens
          const { data: goalies } = await supabase
            .from('show_goalie_onlycurrentday')
            .select('salary')
            .in('playerid', playerIds);

          if (goalies) {
            totalSalaryMain += goalies.reduce((sum, g) => sum + (g.salary || 0), 0);
          }

          const maxSalaryCap = settings?.max_salary_cap || 81500000;
          if (totalSalaryMain > maxSalaryCap) {
            userViolations.push({
              type: 'salary_cap_exceeded',
              details: {
                actual: formatSalary(totalSalaryMain),
                max: formatSalary(maxSalaryCap),
                excess: formatSalary(totalSalaryMain - maxSalaryCap)
              }
            });
          }
        }

        // Vérifier le plafond salarial (réserve, excluant les joueurs LTIR)
        const { data: reservePlayers } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', leagueUser.user_id)
          .eq('isnhl', false)
          .eq('is_injured', false);

        if (reservePlayers && reservePlayers.length > 0) {
          const playerIds = reservePlayers.map(p => p.player_id);
          
          let totalSalaryReserve = 0;

          // Salaires des joueurs (skaters)
          const { data: skaters } = await supabase
            .from('show_player_onlycurrentday')
            .select('salary')
            .in('playerid', playerIds);

          if (skaters) {
            totalSalaryReserve += skaters.reduce((sum, p) => sum + (p.salary || 0), 0);
          }

          // Salaires des gardiens
          const { data: goalies } = await supabase
            .from('show_goalie_onlycurrentday')
            .select('salary')
            .in('playerid', playerIds);

          if (goalies) {
            totalSalaryReserve += goalies.reduce((sum, g) => sum + (g.salary || 0), 0);
          }

          const maxSalaryCapReserve = settings?.max_salary_cap_reserve || 5000000;
          if (totalSalaryReserve > maxSalaryCapReserve) {
            userViolations.push({
              type: 'salary_cap_reserve_exceeded',
              details: {
                actual: formatSalary(totalSalaryReserve),
                max: formatSalary(maxSalaryCapReserve),
                excess: formatSalary(totalSalaryReserve - maxSalaryCapReserve)
              }
            });
          }
        }

        // Vérifier le nombre de joueurs en réserve (excluant les joueurs LTIR)
        const { data: reservePlayersCount } = await supabase
          .from('league_players_user')
          .select('player_id', { count: 'exact' })
          .eq('league_id', selectedLeague.id)
          .eq('user_id', leagueUser.user_id)
          .eq('isnhl', false)
          .eq('is_injured', false);

        const maxReserve = settings?.number_reserve;
        if (maxReserve && maxReserve > 0) {
          const actualReserve = reservePlayersCount?.length || 0;
          if (actualReserve > maxReserve) {
            userViolations.push({
              type: 'too_many_reserve_players',
              details: {
                actual: actualReserve,
                max: maxReserve,
                excess: actualReserve - maxReserve
              }
            });
          }
        }

        // Si l'utilisateur a des violations, l'ajouter à la liste
        if (userViolations.length > 0) {
          foundViolations.push({
            user_id: leagueUser.user_id,
            username: username,
            violations: userViolations
          });
        }
      }

      setViolations(foundViolations);
    } catch (error) {
      console.error('Error checking violations:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la vérification des violations' : 'Error checking violations');
    } finally {
      setLoading(false);
    }
  }

  function getViolationBadgeClass(type: string): string {
    if (type.includes('salary_cap')) return 'bg-danger';
    if (type.includes('too_many')) return 'bg-warning text-dark';
    return 'bg-info';
  }

  // Ne pas afficher pour les ligues standard_flexible
  if (isStandardFlexible) return null;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{language === 'fr' ? 'Chargement...' : 'Loading...'}</span>
        </div>
        <p className="text-muted mt-3">
          {language === 'fr' ? 'Vérification des alignements...' : 'Checking rosters...'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            {language === 'fr' ? 'Violations d\'alignement' : 'Roster Violations'}
          </h5>
          <p className="text-muted small mb-0">
            {language === 'fr' 
              ? 'Suivi des alignements non conformes aux règlements de la ligue' 
              : 'Track lineup violations against league rules'}
          </p>
        </div>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => checkViolations()}
          disabled={loading}
        >
          <RefreshCw size={16} className="me-1" />
          {language === 'fr' ? 'Actualiser' : 'Refresh'}
        </button>
      </div>

      {violations.length === 0 ? (
        <div className="text-center py-5">
          <CheckCircle className="text-success mb-3" size={48} />
          <h5>
            {language === 'fr' 
              ? 'Aucune violation détectée' 
              : 'No violations detected'}
          </h5>
          <p className="text-muted">
            {language === 'fr' 
              ? 'Tous les alignements sont conformes aux règlements.' 
              : 'All lineups are compliant with league rules.'}
          </p>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12 mb-3">
            <div className="alert alert-warning d-flex align-items-center" role="alert">
              <AlertTriangle size={20} className="me-2" />
              <div>
                <strong>
                  {violations.length} {language === 'fr' ? 'équipe(s)' : 'team(s)'}
                </strong>{' '}
                {language === 'fr' 
                  ? 'en violation des règlements' 
                  : 'in violation of league rules'}
              </div>
            </div>
          </div>

          {violations.map((violation) => (
            <div key={violation.user_id} className="col-12">
              <div className="card border-warning">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="mb-1">
                        <AlertTriangle size={18} className="text-warning me-2" />
                        {violation.username}
                      </h6>
                      <small className="text-muted">
                        {violation.violations.length} {language === 'fr' ? 'violation(s)' : 'violation(s)'}
                      </small>
                    </div>
                  </div>

                  {violation.violations.map((v, index) => (
                    <div key={index} className="mb-3">
                      <div className="mb-2">
                        <span className={`badge ${getViolationBadgeClass(v.type)} me-2`}>
                          {violationMessages[v.type] || v.type}
                        </span>
                      </div>

                      <div className="bg-light rounded p-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-semibold">{language === 'fr' ? 'Actuel:' : 'Current:'}</span>
                          <span className="text-danger fw-bold">
                            {v.details.actual}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-semibold">
                            {v.type.includes('not_enough') 
                              ? (language === 'fr' ? 'Requis:' : 'Required:')
                              : (language === 'fr' ? 'Maximum:' : 'Maximum:')}
                          </span>
                          <span>{v.details.max}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold">
                            {v.type.includes('not_enough')
                              ? (language === 'fr' ? 'Manque:' : 'Missing:')
                              : (language === 'fr' ? 'Dépassement:' : 'Excess:')}
                          </span>
                          <span className="text-danger fw-bold">
                            {v.details.excess}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
